/**
 * UserForm Component - Create and edit user form
 *
 * Supports create and edit modes with role selection via checkboxes.
 *
 * Requirements: 2.2, 2.3
 */

import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Spinner, SpinnerSize } from '@fluentui/react';
import type { AppRole } from '@shared/auth';
import type { ManagedUser, CreateUserRequest } from '../../api';
import styles from './UserForm.module.css';

/**
 * Role configuration for display
 */
interface RoleConfig {
  id: AppRole;
  name: string;
  description: string;
}

const ROLE_CONFIGS: RoleConfig[] = [
  {
    id: 'admin',
    name: 'Administrator',
    description: 'Full access to user management and all applications',
  },
  {
    id: 'policy_navigator',
    name: 'Policy Navigator',
    description: 'Access to the Policy Navigator application for querying regulations',
  },
  {
    id: 'evidence_extractor',
    name: 'Evidence Extractor',
    description: 'Access to the Evidence Extractor application for document analysis',
  },
];

export interface UserFormProps {
  /** Mode: create or edit */
  mode: 'create' | 'edit';
  /** Initial user data (for edit mode) */
  initialData?: ManagedUser;
  /** Submit handler */
  onSubmit: (data: CreateUserRequest) => Promise<void>;
  /** Whether form is submitting */
  isSubmitting?: boolean;
  /** Error message to display */
  error?: string | null;
}

interface FormErrors {
  email?: string;
  name?: string;
  roles?: string;
  password?: string;
}

/**
 * Validate email format
 */
function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validate password strength
 */
function isValidPassword(password: string): boolean {
  // At least 8 characters, with at least one uppercase, one lowercase, one number, and one special character
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
  return passwordRegex.test(password);
}

/**
 * Validate form data
 */
export function validateUserForm(data: {
  email: string;
  name: string;
  roles: AppRole[];
  password?: string;
}, isCreateMode: boolean = false): FormErrors {
  const errors: FormErrors = {};

  if (!data.email.trim()) {
    errors.email = 'Email is required';
  } else if (!isValidEmail(data.email)) {
    errors.email = 'Please enter a valid email address';
  }

  if (!data.name.trim()) {
    errors.name = 'Name is required';
  } else if (data.name.trim().length < 2) {
    errors.name = 'Name must be at least 2 characters';
  }

  if (data.roles.length === 0) {
    errors.roles = 'At least one role must be selected';
  }

  // Password validation for create mode
  if (isCreateMode) {
    if (!data.password || !data.password.trim()) {
      errors.password = 'Password is required';
    } else if (data.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    } else if (!isValidPassword(data.password)) {
      errors.password = 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character';
    }
  }

  return errors;
}

/**
 * UserForm component for creating and editing users
 */
export const UserForm: React.FC<UserFormProps> = ({
  mode,
  initialData,
  onSubmit,
  isSubmitting = false,
  error,
}) => {
  const [email, setEmail] = useState(initialData?.email ?? '');
  const [name, setName] = useState(initialData?.name ?? '');
  const [password, setPassword] = useState('');
  const [roles, setRoles] = useState<AppRole[]>(initialData?.roles ?? []);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const handleRoleToggle = useCallback((roleId: AppRole) => {
    setRoles((prev) =>
      prev.includes(roleId)
        ? prev.filter((r) => r !== roleId)
        : [...prev, roleId]
    );
    setTouched((prev) => ({ ...prev, roles: true }));
  }, []);

  const handleBlur = useCallback((field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      const formData = {
        email: email.trim(),
        name: name.trim(),
        roles,
        ...(mode === 'create' ? { password: password.trim() || '' } : {})
      } as CreateUserRequest;
      const validationErrors = validateUserForm(formData, mode === 'create');

      setErrors(validationErrors);
      setTouched({ email: true, name: true, roles: true, password: true });

      if (Object.keys(validationErrors).length > 0) {
        return;
      }

      await onSubmit(formData);
    },
    [email, name, password, roles, onSubmit, mode]
  );

  const isEditMode = mode === 'edit';

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      {error && (
        <div className={styles.formError}>
          <p className={styles.formErrorText}>{error}</p>
        </div>
      )}

      <div className={styles.fieldGroup}>
        <label htmlFor="email" className={styles.label}>
          Email<span className={styles.required}>*</span>
        </label>
        <input
          id="email"
          type="email"
          className={`${styles.input} ${touched.email && errors.email ? styles.inputError : ''}`}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={() => handleBlur('email')}
          disabled={isEditMode || isSubmitting}
          placeholder="user@example.com"
          data-testid="email-input"
        />
        {touched.email && errors.email && (
          <p className={styles.errorText}>{errors.email}</p>
        )}
      </div>

      <div className={styles.fieldGroup}>
        <label htmlFor="name" className={styles.label}>
          Name<span className={styles.required}>*</span>
        </label>
        <input
          id="name"
          type="text"
          className={`${styles.input} ${touched.name && errors.name ? styles.inputError : ''}`}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => handleBlur('name')}
          disabled={isSubmitting}
          placeholder="John Doe"
          data-testid="name-input"
        />
        {touched.name && errors.name && (
          <p className={styles.errorText}>{errors.name}</p>
        )}
      </div>

      {mode === 'create' && (
        <div className={styles.fieldGroup}>
          <label htmlFor="password" className={styles.label}>
            Password<span className={styles.required}>*</span>
          </label>
          <input
            id="password"
            type="password"
            className={`${styles.input} ${touched.password && errors.password ? styles.inputError : ''}`}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onBlur={() => handleBlur('password')}
            disabled={isSubmitting}
            placeholder="Enter a secure password"
            data-testid="password-input"
          />
          {touched.password && errors.password && (
            <p className={styles.errorText}>{errors.password}</p>
          )}
          <p className={styles.helpText}>
            Password must be at least 8 characters with uppercase, lowercase, number, and special character.
          </p>
        </div>
      )}

      <div className={styles.rolesGroup}>
        <span className={styles.rolesLabel}>
          Roles<span className={styles.required}>*</span>
        </span>
        <div className={styles.rolesList} role="group" aria-label="User roles">
          {ROLE_CONFIGS.map((role) => (
            <label
              key={role.id}
              className={styles.roleItem}
              htmlFor={`role-${role.id}`}
            >
              <input
                id={`role-${role.id}`}
                type="checkbox"
                className={styles.roleCheckbox}
                checked={roles.includes(role.id)}
                onChange={() => handleRoleToggle(role.id)}
                disabled={isSubmitting}
                data-testid={`role-checkbox-${role.id}`}
              />
              <div className={styles.roleInfo}>
                <p className={styles.roleName}>{role.name}</p>
                <p className={styles.roleDescription}>{role.description}</p>
              </div>
            </label>
          ))}
        </div>
        {touched.roles && errors.roles && (
          <p className={styles.errorText}>{errors.roles}</p>
        )}
      </div>

      <div className={styles.actions}>
        <button
          type="submit"
          className={styles.submitButton}
          disabled={isSubmitting}
          data-testid="submit-button"
        >
          {isSubmitting ? (
            <>
              <Spinner size={SpinnerSize.xSmall} />
              <span>{isEditMode ? 'Saving...' : 'Creating...'}</span>
            </>
          ) : (
            <span>{isEditMode ? 'Save Changes' : 'Create User'}</span>
          )}
        </button>
        <Link to="/admin" className={styles.cancelButton}>
          Cancel
        </Link>
      </div>
    </form>
  );
};
